import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiXCircle, FiUserPlus } from 'react-icons/fi';
import InputMask from 'react-input-mask';
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import styles from './CadastroVeiculos.module.css';
import PageHeader from '../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';

const CadastroVeiculos = () => {
    const initialState = {
        tipoVeiculo: '', marca: '', modelo: '', anoFabricacao: '', anoModelo: '',
        placa: '', renavam: '', chassi: '', cor: '', potenciaMotor: '',
        quilometragem: '', tipoCombustivel: '', capacidadeTanque: '',
        codigo_cc: '',
        observacoes: '', operador_id: '',
        vencimentoAET: '',
        vencimento_aet_estadual: '',
        vencimentoCronotacografo: '',
        vencimentoDocumentos: '',
        horimetro: '',
        tipo_colheita: '',
        limite_manutencao: '',
        tipo_limite_manutencao: ''
    };

    const initialOperadorState = {
        username: '', password: '', confirmPassword: '',
        nome_completo: '', cpf: '', numero_habilitacao: ''
    };

    const [formData, setFormData] = useState(initialState);
    const [operadores, setOperadores] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [informarVencimentos, setInformarVencimentos] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [showOperadorModal, setShowOperadorModal] = useState(false);
    const [novoOperador, setNovoOperador] = useState(initialOperadorState);
    const [operadorModalErrors, setOperadorModalErrors] = useState({});
    const [isSavingOperador, setIsSavingOperador] = useState(false);

    const fetchOperadores = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/operadores`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setOperadores(response.data);
        } catch (error) {
            console.error("Erro ao buscar operadores:", error);
            Swal.fire('Erro!', 'Não foi possível carregar a lista de operadores.', 'error');
        }
    }, []);

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();
        setPageLoading(true);

        const loadInitialData = async () => {
            await fetchOperadores();

            const endTime = Date.now();
            const duration = endTime - startTime;
            const remainingTime = MIN_LOADING_TIME - duration;

            if (remainingTime > 0) {
                setTimeout(() => setPageLoading(false), remainingTime);
            } else {
                setPageLoading(false);
            }
        };

        loadInitialData();
    }, [fetchOperadores]);
    
    useEffect(() => {
        const veiculoSemCombustivel = ['SEMIRREBOQUE', 'MAQUINA'];
        const veiculoSemPlaca = ['MAQUINA', 'TRATOR'];

        if (veiculoSemCombustivel.includes(formData.tipoVeiculo)) {
            setFormData(prev => ({
                ...prev,
                potenciaMotor: prev.tipoVeiculo === 'SEMIRREBOQUE' ? '' : prev.potenciaMotor,
                tipoCombustivel: '',
                capacidadeTanque: '',
            }));
        }

        if (veiculoSemPlaca.includes(formData.tipoVeiculo)) {
            setFormData(prev => ({
                ...prev,
                placa: '',
                renavam: '',
                quilometragem: '', 
            }));
        }
        
        if (formData.tipoVeiculo !== 'MAQUINA') {
            setFormData(prev => ({ ...prev, horimetro: '' }));
        }

    }, [formData.tipoVeiculo]);


    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        const uppercaseFields = ['placa', 'chassi', 'tipoVeiculo', 'tipoCombustivel', 'tipo_colheita'];
        const finalValue = uppercaseFields.includes(name) ? value.toUpperCase() : value;
        setFormData(prev => ({ ...prev, [name]: finalValue }));
        if (errors?.[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [errors]);

    const handleVencimentosCheckbox = useCallback((e) => {
        const { checked } = e.target;
        setInformarVencimentos(checked);
        if (!checked) {
            setFormData(prev => ({
                ...prev,
                vencimentoAET: '',
                vencimento_aet_estadual: '',
                vencimentoCronotacografo: '',
                vencimentoDocumentos: ''
            }));
        }
    }, []);

    const validateForm = useCallback(() => {
        const newErrors = {};
        const currentYear = new Date().getFullYear();
        const veiculoSemPlaca = ['MAQUINA', 'TRATOR'];
        const veiculoSemCombustivel = ['SEMIRREBOQUE', 'MAQUINA'];
        const tiposColheitaValidos = ['HARVESTER', 'SKIDDER', 'FORWARDER', 'TRATOR GUINCHO', 'TRATOR', 'OUTRO'];

        if (!formData.tipoVeiculo) newErrors.tipoVeiculo = 'Tipo de veículo é obrigatório.';
        if (!formData.marca) newErrors.marca = 'Marca é obrigatória.';
        if (!formData.modelo) newErrors.modelo = 'Modelo é obrigatório.';
        if (!formData.anoFabricacao || +formData.anoFabricacao < 1900 || +formData.anoFabricacao > currentYear + 1) newErrors.anoFabricacao = 'Ano de Fabricação inválido.';
        if (!formData.anoModelo || +formData.anoModelo < +formData.anoFabricacao) newErrors.anoModelo = 'Ano Modelo não pode ser anterior à fabricação.';
        if (formData.chassi?.length !== 17) newErrors.chassi = 'Chassi deve ter 17 caracteres.';
        if (!formData.cor) newErrors.cor = 'Cor é obrigatória.';
        
        if (formData.tipo_colheita && !tiposColheitaValidos.includes(formData.tipo_colheita)) {
            newErrors.tipo_colheita = 'Tipo de Colheita inválido.';
        }
        
        if (formData.tipoVeiculo === 'MAQUINA') {
            if (formData.horimetro === '' || +formData.horimetro < 0) newErrors.horimetro = 'Horímetro inválido.';
        } else {
             if (formData.quilometragem === '' || +formData.quilometragem < 0) newErrors.quilometragem = 'Quilometragem inválida.';
        }
        
        if (!veiculoSemPlaca.includes(formData.tipoVeiculo)) {
            if (!formData.placa) newErrors.placa = 'Placa é obrigatória.';
            if (formData.renavam?.replace(/_/g, '').length !== 11) newErrors.renavam = 'RENAVAM deve ter 11 dígitos.';
        }

        if (!veiculoSemCombustivel.includes(formData.tipoVeiculo)) {
            if (!formData.tipoCombustivel) newErrors.tipoCombustivel = 'Tipo de Combustível é obrigatório.';
            if (!formData.capacidadeTanque || +formData.capacidadeTanque <= 0) newErrors.capacidadeTanque = 'Capacidade do Tanque inválida.';
            if (formData.potenciaMotor && formData.potenciaMotor.length > 50) newErrors.potenciaMotor = 'Potência do Motor deve ter no máximo 50 caracteres.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    const clearForm = () => {
        setFormData(initialState);
        setInformarVencimentos(false);
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire('Erro de Validação', 'Por favor, corrija os campos destacados.', 'error');
            return;
        }

        setIsLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_BASE_URL}/api/veiculos`, formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            Swal.fire('Sucesso!', response.data.message || 'Veículo cadastrado com sucesso!', 'success');
            clearForm();

        } catch (error) {
            console.error('Erro ao cadastrar veículo:', error);
            const errorMessage = error.response?.data?.error || 'Não foi possível cadastrar o veículo.';
            Swal.fire('Erro!', errorMessage, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearForm = () => {
        Swal.fire({
            title: 'Limpar Formulário?',
            text: "Todos os dados preenchidos serão perdidos.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, limpar!',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                clearForm();
            }
        });
    };

    const handleNovoOperadorChange = (e) => {
        const { name, value } = e.target;
        setNovoOperador(prev => ({ ...prev, [name]: value }));
        if (operadorModalErrors?.[name]) {
            setOperadorModalErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleNovoOperadorSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!novoOperador.nome_completo) newErrors.nome_completo = "Nome completo é obrigatório.";
        if (!novoOperador.username) newErrors.username = "Nome de usuário é obrigatório.";
        if (!novoOperador.password) newErrors.password = "Senha é obrigatória.";
        if (novoOperador.password?.length < 6) newErrors.password = "Senha deve ter no mínimo 6 caracteres.";
        if (novoOperador.password !== novoOperador.confirmPassword) newErrors.confirmPassword = "As senhas não coincidem.";
        if (!/^\d{11}$/.test(novoOperador.cpf?.replace(/\D/g, ''))) newErrors.cpf = "CPF inválido.";
        if (!/^\d+$/.test(novoOperador.numero_habilitacao)) newErrors.numero_habilitacao = "CNH deve conter apenas números.";

        if (Object.keys(newErrors).length > 0) {
            setOperadorModalErrors(newErrors);
            return;
        }

        setIsSavingOperador(true);
        setOperadorModalErrors({});

        try {
            const token = localStorage.getItem('token');
            const payload = {
                username: novoOperador.username,
                password: novoOperador.password,
                tipo_usuario: 'operador',
                nome_completo: novoOperador.nome_completo,
                cpf: novoOperador.cpf?.replace(/\D/g, ''),
                numero_habilitacao: novoOperador.numero_habilitacao,
            };

            const response = await axios.post(`${API_BASE_URL}/api/usuarios`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const newUserId = response.data.user?.id;

            Swal.fire('Sucesso!', 'Operador cadastrado com sucesso!', 'success');

            await fetchOperadores();
            if (newUserId) {
                setFormData(prev => ({ ...prev, operador_id: newUserId }));
            }

            setShowOperadorModal(false);
            setNovoOperador(initialOperadorState);

        } catch (error) {
            console.error('Erro ao cadastrar operador:', error);
            const errorMessage = error.response?.data?.error || 'Não foi possível cadastrar o operador.';
            setOperadorModalErrors({ api: errorMessage });
        } finally {
            setIsSavingOperador(false);
        }
    };

    const getInputClassName = (fieldName) => `${styles.formInput} ${errors?.[fieldName] ? styles.inputError : ''}`;
    const getSelectClassName = (fieldName) => `${styles.formSelect} ${errors?.[fieldName] ? styles.inputError : ''}`;
    const getTextareaClassName = (fieldName) => `${styles.formTextarea} ${errors?.[fieldName] ? styles.inputError : ''}`;

    const veiculoSemPlaca = ['MAQUINA', 'TRATOR'];
    const veiculoSemCombustivel = ['SEMIRREBOQUE', 'MAQUINA'];


    return (
        <div className={styles.container}>
            {pageLoading && <LoadingOverlay text="Carregando..." />} 

            {!pageLoading && (
                <>
                    <PageHeader 
                        title="Cadastrar Novo Veículo"
                        subtitle="Preencha os dados abaixo para registrar um novo veículo na frota."
                        backTo="/sistema-frota"
                    />

                    <form onSubmit={handleSubmit} className={styles.form} noValidate>
                        <section className={styles.formSection}>
                            <h2>Dados Principais</h2>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="tipoVeiculo">Tipo de Veículo *</label>
                                    <select id="tipoVeiculo" name="tipoVeiculo" value={formData.tipoVeiculo} onChange={handleChange} className={getSelectClassName('tipoVeiculo')}>
                                        <option value="">Selecione...</option>
                                        <option value="CARRO">CARRO</option>
                                        <option value="MOTO">MOTO</option>
                                        <option value="CAMINHAO">CAMINHÃO</option>
                                        <option value="TRATOR">TRATOR</option>
                                        <option value="MAQUINA">MÁQUINA</option>
                                        <option value="SEMIRREBOQUE">SEMIRREBOQUE</option>
                                    </select>
                                    {errors?.tipoVeiculo && <span className={styles.errorMessage}>{errors.tipoVeiculo}</span>}
                                </div>
                                
                                <div className={styles.formGroup}>
                                    <label htmlFor="tipo_colheita">Função na Colheita (Opcional)</label>
                                    <select 
                                        id="tipo_colheita" 
                                        name="tipo_colheita" 
                                        value={formData.tipo_colheita} 
                                        onChange={handleChange} 
                                        className={getSelectClassName('tipo_colheita')}
                                    >
                                        <option value="">Nenhuma...</option>
                                        <option value="HARVESTER">HARVESTER (Corte)</option>
                                        <option value="SKIDDER">SKIDDER (Arrasto)</option>
                                        <option value="FORWARDER">FORWARDER (Baldeio)</option>
                                        <option value="TRATOR GUINCHO">TRATOR GUINCHO</option>
                                        <option value="TRATOR">TRATOR</option>
                                        <option value="OUTRO">OUTRO (MÁQUINA)</option>
                                    </select>
                                    {errors?.tipo_colheita && <span className={styles.errorMessage}>{errors.tipo_colheita}</span>}
                                </div>
                                
                                <div className={styles.formGroup}>
                                    <label htmlFor="marca">Marca *</label>
                                    <input type="text" id="marca" name="marca" value={formData.marca} onChange={handleChange} className={getInputClassName('marca')} placeholder="Ex: Fiat" />
                                    {errors?.marca && <span className={styles.errorMessage}>{errors.marca}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="modelo">Modelo *</label>
                                    <input type="text" id="modelo" name="modelo" value={formData.modelo} onChange={handleChange} className={getInputClassName('modelo')} placeholder="Ex: Ford Ranger" />
                                    {errors?.modelo && <span className={styles.errorMessage}>{errors.modelo}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="anoFabricacao">Ano Fabricação *</label>
                                    <input type="number" id="anoFabricacao" name="anoFabricacao" value={formData.anoFabricacao} onChange={handleChange} className={getInputClassName('anoFabricacao')} placeholder="AAAA" />
                                    {errors?.anoFabricacao && <span className={styles.errorMessage}>{errors.anoFabricacao}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="anoModelo">Ano Modelo *</label>
                                    <input type="number" id="anoModelo" name="anoModelo" value={formData.anoModelo} onChange={handleChange} className={getInputClassName('anoModelo')} placeholder="AAAA" />
                                    {errors?.anoModelo && <span className={styles.errorMessage}>{errors.anoModelo}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="cor">Cor *</label>
                                    <input type="text" id="cor" name="cor" value={formData.cor} onChange={handleChange} className={getInputClassName('cor')} placeholder="Ex: Branco" />
                                    {errors?.cor && <span className={styles.errorMessage}>{errors.cor}</span>}
                                </div>
                                <div className={`${styles.formGroup} ${formData.tipoVeiculo === 'SEMIRREBOQUE' ? styles.disabledContent : ''}`}>
                                    <label htmlFor="potenciaMotor">Potência do Motor</label>
                                    <input
                                        type="text"
                                        id="potenciaMotor"
                                        name="potenciaMotor"
                                        value={formData.potenciaMotor}
                                        onChange={handleChange}
                                        className={getInputClassName('potenciaMotor')}
                                        placeholder="Ex: 110cv"
                                        maxLength="50"
                                        disabled={formData.tipoVeiculo === 'SEMIRREBOQUE'}
                                    />
                                    {errors?.potenciaMotor && <span className={styles.errorMessage}>{errors.potenciaMotor}</span>}
                                </div>
                            </div>
                        </section>

                        <section className={styles.formSection}>
                            <h2>Documentação e Operador</h2>
                            <div className={styles.formGrid}>
                                {!veiculoSemPlaca.includes(formData.tipoVeiculo) && (
                                    <>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="placa">Placa *</label>
                                            <input
                                                type="text"
                                                id="placa"
                                                name="placa"
                                                value={formData.placa}
                                                onChange={handleChange}
                                                className={getInputClassName('placa')}
                                                placeholder="Digite a placa"
                                                maxLength="8"
                                            />
                                            {errors?.placa && <span className={styles.errorMessage}>{errors.placa}</span>}
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="renavam">RENAVAM *</label>
                                            <InputMask mask="99999999999" maskChar={null} id="renavam" name="renavam" value={formData.renavam} onChange={handleChange} className={getInputClassName('renavam')} placeholder="11 dígitos" />
                                            {errors?.renavam && <span className={styles.errorMessage}>{errors.renavam}</span>}
                                        </div>
                                    </>
                                )}
                                <div className={styles.formGroup}>
                                    <label htmlFor="operador_id">Motorista/Operador Responsável</label>
                                    <div className={styles.operadorSelectWrapper}>
                                        <select id="operador_id" name="operador_id" value={formData.operador_id} onChange={handleChange} className={getSelectClassName('operador_id')}>
                                            <option value="">Nenhum motorista/operador</option>
                                            {operadores.map(operador => (
                                                <option key={operador.id} value={operador.id}>
                                                    {operador.nome_completo}
                                                </option>
                                            ))}
                                        </select>
                                        <button type="button" className={styles.btnAddOperador} onClick={() => setShowOperadorModal(true)} title="Adicionar Novo Operador">
                                            <FiUserPlus />
                                        </button>
                                    </div>
                                </div>
                                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                    <label htmlFor="chassi">Chassi *</label>
                                    <input type="text" id="chassi" name="chassi" value={formData.chassi} onChange={handleChange} className={getInputClassName('chassi')} maxLength="17" placeholder="17 caracteres alfanuméricos" />
                                    {errors?.chassi && <span className={styles.errorMessage}>{errors.chassi}</span>}
                                </div>
                            </div>
                        </section>
                        
                        <section className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <h2>Controle de Vencimentos</h2>
                                <div className={styles.checkboxGroup}>
                                    <input
                                        type="checkbox"
                                        id="informarVencimentos"
                                        name="informarVencimentos"
                                        checked={informarVencimentos}
                                        onChange={handleVencimentosCheckbox}
                                    />
                                    <label htmlFor="informarVencimentos">Deseja informar vencimentos?</label>
                                </div>
                            </div>
                            <div className={`${styles.formGrid} ${!informarVencimentos ? styles.disabledContent : ''}`}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="vencimentoAET">Vencimento AET (Nacional)</label>
                                    <input type="date" id="vencimentoAET" name="vencimentoAET" value={formData.vencimentoAET} onChange={handleChange} className={getInputClassName('vencimentoAET')} disabled={!informarVencimentos} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="vencimento_aet_estadual">Vencimento AET (Estadual)</label>
                                    <input type="date" id="vencimento_aet_estadual" name="vencimento_aet_estadual" value={formData.vencimento_aet_estadual} onChange={handleChange} className={getInputClassName('vencimento_aet_estadual')} disabled={!informarVencimentos} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="vencimentoCronotacografo">Vencimento Cronotacógrafo</label>
                                    <input type="date" id="vencimentoCronotacografo" name="vencimentoCronotacografo" value={formData.vencimentoCronotacografo} onChange={handleChange} className={getInputClassName('vencimentoCronotacografo')} disabled={!informarVencimentos} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="vencimentoDocumentos">Vencimento Licenças/Documentos</label>
                                    <input type="date" id="vencimentoDocumentos" name="vencimentoDocumentos" value={formData.vencimentoDocumentos} onChange={handleChange} className={getInputClassName('vencimentoDocumentos')} disabled={!informarVencimentos} />
                                </div>
                            </div>
                        </section>
                        
                        <section className={styles.formSection}>
                            <h2>Detalhes Operacionais e Alertas</h2>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="quilometragem">Quilometragem (KM)</label>
                                    <input
                                        type="number" id="quilometragem" name="quilometragem"
                                        value={formData.quilometragem} onChange={handleChange}
                                        className={getInputClassName('quilometragem')} placeholder="0" min="0"
                                        disabled={formData.tipoVeiculo === 'MAQUINA'}
                                    />
                                    {errors?.quilometragem && <span className={styles.errorMessage}>{errors.quilometragem}</span>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="horimetro">Horímetro (Horas)</label>
                                    <input
                                        type="number" id="horimetro" name="horimetro"
                                        value={formData.horimetro} onChange={handleChange}
                                        className={getInputClassName('horimetro')} placeholder="0" min="0"
                                    />
                                    {errors?.horimetro && <span className={styles.errorMessage}>{errors.horimetro}</span>}
                                </div>
                                
                                <div className={`${styles.formGroup} ${veiculoSemCombustivel.includes(formData.tipoVeiculo) ? styles.disabledContent : ''}`}>
                                    <label htmlFor="tipoCombustivel">Combustível *</label>
                                    <select
                                        id="tipoCombustivel"
                                        name="tipoCombustivel"
                                        value={formData.tipoCombustivel}
                                        onChange={handleChange}
                                        className={getSelectClassName('tipoCombustivel')}
                                        disabled={veiculoSemCombustivel.includes(formData.tipoVeiculo)}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="GASOLINA">GASOLINA</option>
                                        <option value="ETANOL">ETANOL</option>
                                        <option value="DIESEL">DIESEL</option>
                                        <option value="FLEX">FLEX (GASOLINA/ETANOL)</option>
                                    </select>
                                    {errors?.tipoCombustivel && <span className={styles.errorMessage}>{errors.tipoCombustivel}</span>}
                                </div>
                                <div className={`${styles.formGroup} ${veiculoSemCombustivel.includes(formData.tipoVeiculo) ? styles.disabledContent : ''}`}>
                                    <label htmlFor="capacidadeTanque">Tanque (Litros) *</label>
                                    <input
                                        type="number"
                                        id="capacidadeTanque"
                                        name="capacidadeTanque"
                                        value={formData.capacidadeTanque}
                                        onChange={handleChange}
                                        className={getInputClassName('capacidadeTanque')}
                                        placeholder="Ex: 50"
                                        min="0"
                                        disabled={veiculoSemCombustivel.includes(formData.tipoVeiculo)}
                                    />
                                    {errors?.capacidadeTanque && <span className={styles.errorMessage}>{errors.capacidadeTanque}</span>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="limite_manutencao">Limite de Troca/Aviso (Km ou Horas)</label>
                                    <input 
                                        type="number" id="limite_manutencao" name="limite_manutencao" 
                                        value={formData.limite_manutencao} onChange={handleChange} 
                                        className={styles.formInput} placeholder="Ex: 1000" 
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="tipo_limite_manutencao">Tipo de Limite</label>
                                    <select 
                                        id="tipo_limite_manutencao" name="tipo_limite_manutencao" 
                                        value={formData.tipo_limite_manutencao} onChange={handleChange} 
                                        className={styles.formSelect}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="quilometragem">Quilometragem (Km)</option>
                                        <option value="horimetro">Horímetro (Horas)</option>
                                    </select>
                                </div>
                                
                                <div className={styles.formGroup}>
                                    <label htmlFor="codigo_cc">Código CC (Centro de Custo)</label>
                                    <input type="text" id="codigo_cc" name="codigo_cc" value={formData.codigo_cc} onChange={handleChange} className={getInputClassName('codigo_cc')} placeholder="Opcional" />
                                    {errors?.codigo_cc && <span className={styles.errorMessage}>{errors.codigo_cc}</span>}
                                </div>

                                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                    <label htmlFor="observacoes">Observações</label>
                                    <textarea id="observacoes" name="observacoes" value={formData.observacoes} onChange={handleChange} className={getTextareaClassName('observacoes')} rows="4" placeholder="Informações adicionais..."></textarea>
                                </div>
                            </div>
                        </section>
                        
                        <div className={styles.formActions}>
                            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={handleClearForm} disabled={isLoading}>
                                <FiXCircle className={styles.btnIcon} /> Limpar
                            </button>
                            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isLoading}>
                                {isLoading ? <span className={styles.spinner}></span> : <FiSave className={styles.btnIcon} />}
                                {isLoading ? 'Salvando...' : 'Salvar Veículo'}
                            </button>
                        </div>
                    </form>
                    
                    {showOperadorModal && (
                        <div className={styles.operadorModalOverlay}>
                            <div className={styles.operadorModalContainer}>
                                <header className={styles.operadorModalHeader}>
                                    <h3>Cadastrar Novo Operador</h3>
                                    <button className={styles.operadorModalCloseBtn} onClick={() => setShowOperadorModal(false)}>
                                        <FiXCircle />
                                    </button>
                                </header>
                                <div className={styles.operadorModalBody}>
                                    <form onSubmit={handleNovoOperadorSubmit} id="novo-motorista-form" noValidate>
                                        {operadorModalErrors?.api && <div className={styles.modalErrorMessage}>{operadorModalErrors.api}</div>}
                                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="nome_completo">Nome Completo *</label>
                                            <input type="text" id="nome_completo" name="nome_completo" value={novoOperador.nome_completo} onChange={handleNovoOperadorChange} className={styles.formInput} required />
                                            {operadorModalErrors?.nome_completo && <span className={styles.errorMessage}>{operadorModalErrors.nome_completo}</span>}
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="username">Nome de Usuário *</label>
                                            <input type="text" id="username" name="username" value={novoOperador.username} onChange={handleNovoOperadorChange} className={styles.formInput} required />
                                            {operadorModalErrors?.username && <span className={styles.errorMessage}>{operadorModalErrors.username}</span>}
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="cpf">CPF *</label>
                                            <InputMask mask="999.999.999-99" id="cpf" name="cpf" value={novoOperador.cpf} onChange={handleNovoOperadorChange} className={styles.formInput} required />
                                            {operadorModalErrors?.cpf && <span className={styles.errorMessage}>{operadorModalErrors.cpf}</span>}
                                        </div>
                                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="numero_habilitacao">Nº da Habilitação (CNH) *</label>
                                            <input type="text" id="numero_habilitacao" name="numero_habilitacao" value={novoOperador.numero_habilitacao} onChange={handleNovoOperadorChange} className={styles.formInput} required />
                                            {operadorModalErrors?.numero_habilitacao && <span className={styles.errorMessage}>{operadorModalErrors.numero_habilitacao}</span>}
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="password">Senha *</label>
                                            <input type="password" id="password" name="password" value={novoOperador.password} onChange={handleNovoOperadorChange} className={styles.formInput} required />
                                            {operadorModalErrors?.password && <span className={styles.errorMessage}>{operadorModalErrors.password}</span>}
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="confirmPassword">Confirmar Senha *</label>
                                            <input type="password" id="confirmPassword" name="confirmPassword" value={novoOperador.confirmPassword} onChange={handleNovoOperadorChange} className={styles.formInput} required />
                                            {operadorModalErrors?.confirmPassword && <span className={styles.errorMessage}>{operadorModalErrors.confirmPassword}</span>}
                                        </div>
                                    </form>
                                </div>
                                <footer className={styles.operadorModalFooter}>
                                    <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowOperadorModal(false)} disabled={isSavingOperador}>
                                        Cancelar
                                    </button>
                                    <button type="submit" form="novo-motorista-form" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isSavingOperador}>
                                        {isSavingOperador ? <span className={styles.spinner}></span> : <FiSave />}
                                        {isSavingOperador ? 'Salvando...' : 'Salvar Operador'}
                                    </button>
                                </footer>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CadastroVeiculos;